const getQueriesByLevel = (query_name: string) => {
  const levelQueries = {
    location_position_rate_setting: `
      location {
        primary_billing_group {
          plan
        }
      }
      location_id
    `,
    market_position_rate_setting: `
      market {
        id
        display_name
      }
      with_markup_settings {
        ten99 {
          charge_rate_cents
          charge_blended_rate_cents
          modified_charge_rate_cents
        }
        w2 {
          local {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
          travel {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
        }
      }
      market_id
      last_rate_review
    `,
    state_position_rate_setting: `
      state {
        id
        name
      }
      state_id
    `,
    market_location_type_position_rate_setting: `
      market {
        id
        display_name
      }
      market_id
      location_type {
        id
        label
      }
      with_markup_settings {
        ten99 {
          charge_rate_cents
          charge_blended_rate_cents
          modified_charge_rate_cents
        }
        w2 {
          local {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
          travel {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
        }
      }
      location_type_id
      market_location_type_position_rate_setting_active
    `,
    state_location_type_position_rate_setting: `
      state {
        id
        name
      }
      state_id
      location_type {
        id
        label
      }
      with_markup_settings {
        ten99 {
          charge_rate_cents
          charge_blended_rate_cents
          modified_charge_rate_cents
        }
        w2 {
          local {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
          travel {
            charge_rate_cents
            charge_blended_rate_cents
            modified_charge_rate_cents
          }
        }
      }
      location_type_id
      state_location_type_position_rate_setting_active
    `,
    agency_location_position_rate_setting: `
      agency {
        id
        name
      }
      location_id
      location {
        id
        name
      }
      location {
        primary_billing_group {
          plan
        }
      }
    `,
  };

  return levelQueries[query_name as keyof typeof levelQueries] || '';
};

const commonFieldsForRateStructureSubsection = `
  charge_max_weekly_regular_hours
  charge_seventh_day_rule_enabled
  payout_seventh_day_rule_enabled
  payout_max_weekly_regular_hours
  use_blended_rate
  blended_rates {
    blended_rate_markup
    charge_overtime_multiplier
    payout_overtime_multiplier
    charge_max_daily_regular_hours
    payout_max_daily_regular_hours
    payout_max_daily_overtime_hours
    charge_max_daily_overtime_hours
    charge_double_overtime_multiplier
    payout_double_overtime_multiplier
  }
  non_blended_rates {
    blended_rate_markup
    charge_overtime_multiplier
    payout_overtime_multiplier
    charge_max_daily_regular_hours
    payout_max_daily_regular_hours
    payout_max_daily_overtime_hours
    charge_max_daily_overtime_hours
    charge_double_overtime_multiplier
    payout_double_overtime_multiplier
  }
`;

export const generatePositionalRateSettingsShowViewQuery = (queryName: string) => {
  const formattedQueryName = queryName.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  const isLocationTypePositionRateSetting = queryName.includes('location_type');
  const isAgencyLocationPositionRateSetting = queryName.includes('agency_location');
  const isLocationPositionRateSetting = queryName === 'location_position_rate_setting';
  const isMarketPositionRateSetting = queryName === 'market_position_rate_setting';
  const isStatePositionRateSetting = queryName === 'state_position_rate_setting';
  const supportsGuardrails = isMarketPositionRateSetting || isStatePositionRateSetting;
  const supportsIncrementalMargins = supportsGuardrails || isLocationPositionRateSetting;

  return `
  query ${formattedQueryName}($id: Int!, $include_parents: Boolean = true) {
    ${formattedQueryName}(id: $id, include_parents: $include_parents) {
      id
      active
      audits {
        action
        account {
          name
        }
        audited_changes
        created_at
        id
        user_id
      }
      ${isLocationPositionRateSetting ? `salesforce_generated` : ''}
      created_at
      ${getQueriesByLevel(queryName)}
      ${queryName} {
        agency_charge_rate_cents
        agency_release_lead_time_hours
        can_post_assignment
        can_post_job
        charge_on_call_hourly_rate_cents
        charge_on_call_hourly_rate_currency
        charge_rate_cents
        ${
          isAgencyLocationPositionRateSetting
            ? ''
            : `
          track_unpaid_breaks
          use_agency_charge_rate_cents
          `
        }
        fee_percent
        ${isLocationTypePositionRateSetting ? 'location_type_markup' : ''}
        on_call_fee_percent
        payout_on_call_hourly_rate_cents
        payout_on_call_hourly_rate_currency
        ten99 {
          charge_rate_cents
          charge_blended_rate_cents
          margin_percent_1099
          base_charge_rate_cents
          base_margin_percent
          invoicing_markup
          misc_markup
          modified_charge_rate_cents
          ${commonFieldsForRateStructureSubsection}
          ${
            supportsGuardrails
              ? `
                base_margin_percent_1099_guardrail
                payout_rate_percent_1099_guardrail
              `
              : ''
          }
          ${
            supportsIncrementalMargins
              ? `
                incremental_margin_percent_1099
              `
              : ''
          }
        }
        w2_fee_percent
        w2 {
          social_security_burden_multiplier
          medicare_burden_multiplier
          federal_unemployment_percent_burden_multiplier
          state_unemployment_percent_burden_multiplier
          ${
            supportsGuardrails
              ? `
                base_margin_percent_w2_guardrail
                payout_rate_percent_w2_guardrail
              `
              : ''
          }
          ${
            supportsIncrementalMargins
              ? `
                incremental_margin_percent_w2
              `
              : ''
          }
          local {
            base_margin_percent_w2
            charge_rate_cents
            charge_blended_rate_cents
            total_margin_percent_w2
            base_charge_rate_cents
            invoicing_markup
            modified_charge_rate_cents
            misc_markup
            operational_markup
            ${commonFieldsForRateStructureSubsection}
          }
          travel {
            assignment_bonus_tier1_travel_amount_cents
            assignment_bonus_tier1_travel_amount_tcv_cents
            assignment_bonus_tier2_travel_amount_cents
            assignment_bonus_tier2_travel_amount_tcv_cents
            charge_rate_cents
            charge_blended_rate_cents
            margin_percent_w2_tcv
            margin_percent_w2_travel
            margin_w2_tcv_cents
            base_charge_rate_cents
            base_margin_percent_w2_tcv
            base_margin_percent_w2_travel
            invoicing_markup
            modified_charge_rate_cents
            misc_markup
            operational_markup
            ${commonFieldsForRateStructureSubsection}
          }
        }
      }
      position {
        id
        display_name
      }
      position_id
      settings {
        agency_charge_rate_cents
        agency_release_lead_time_hours
        can_post_assignment
        can_post_job
        charge_on_call_hourly_rate_cents
        charge_on_call_hourly_rate_currency
        charge_rate_cents
        ${
          isAgencyLocationPositionRateSetting
            ? ''
            : `
          track_unpaid_breaks
          use_agency_charge_rate_cents
          `
        }
        fee_percent
        ${isLocationTypePositionRateSetting ? 'location_type_markup' : ''}
        on_call_fee_percent
        payout_on_call_hourly_rate_cents
        payout_on_call_hourly_rate_currency
        ten99 {
          charge_rate_cents
          charge_blended_rate_cents
          margin_percent_1099
          modified_charge_rate_cents
          base_charge_rate_cents
          base_margin_percent
          invoicing_markup
          misc_markup
          ${
            supportsGuardrails
              ? `
                base_margin_percent_1099_guardrail
                payout_rate_percent_1099_guardrail
              `
              : ''
          }
          ${
            supportsIncrementalMargins
              ? `
                incremental_margin_percent_1099
              `
              : ''
          }
          ${commonFieldsForRateStructureSubsection}
        }
        w2_fee_percent
        w2 {
          social_security_burden_multiplier
          medicare_burden_multiplier
          federal_unemployment_percent_burden_multiplier
          state_unemployment_percent_burden_multiplier
          ${
            supportsGuardrails
              ? `
                base_margin_percent_w2_guardrail
                payout_rate_percent_w2_guardrail
              `
              : ''
          }
          ${
            supportsIncrementalMargins
              ? `
                incremental_margin_percent_w2
              `
              : ''
          }
          local {
            base_margin_percent_w2
            charge_rate_cents
            charge_blended_rate_cents
            total_margin_percent_w2
            base_charge_rate_cents
            invoicing_markup
            modified_charge_rate_cents
            misc_markup
            operational_markup
            ${commonFieldsForRateStructureSubsection}
          }
          travel {
            assignment_bonus_tier1_travel_amount_cents
            assignment_bonus_tier1_travel_amount_tcv_cents
            assignment_bonus_tier2_travel_amount_cents
            assignment_bonus_tier2_travel_amount_tcv_cents
            charge_rate_cents
            charge_blended_rate_cents
            margin_percent_w2_tcv
            margin_percent_w2_travel
            margin_w2_tcv_cents
            modified_charge_rate_cents
            base_charge_rate_cents
            base_margin_percent_w2_tcv
            base_margin_percent_w2_travel
            invoicing_markup
            misc_markup
            operational_markup
            ${commonFieldsForRateStructureSubsection}
          }
        }
      }
      specialty {
        id
        label
      }
      specialty_id
      updated_at
    }
  }
  `;
};
